import { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { COLLECTIONS, BUSINESS_INFO } from '../data';
import SectionHeader from './SectionHeader';
import { useLang } from '../context/LangContext';

export default function Collections() {
  const { t } = useLang();
  const gridRef = useRef(null);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.querySelectorAll('.reveal-scale').forEach((c, i) => {
          setTimeout(() => c.classList.add('visible'), i * 90);
        });
        obs.unobserve(el);
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="collections" className="section-padding container">
      <SectionHeader
        tag={t.collectionsTag}
        title={<>{t.collectionsTitle} <em style={{ fontStyle: 'italic', fontWeight: 300 }}>{t.collectionsTitleEm}</em></>}
        desc={t.collectionsDesc}
        id="collections-heading"
      />

      <div ref={gridRef} className="grid-4-col" style={{ marginTop: '3.5rem' }}>
        {COLLECTIONS.map((col, i) => (
          <a
            key={col.id}
            href={BUSINESS_INFO.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="reveal-scale collection-card"
            style={{ textDecoration: 'none', height: 'clamp(280px, 65vw, 440px)', display: 'block' }}
          >
            {/* Main Image */}
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit' }}>
              <img
                src={col.image}
                alt={col.alt}
                loading="lazy"
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  transition: 'transform 0.9s cubic-bezier(0.25,1,0.5,1)',
                  willChange: 'transform',
                }}
              />
            </div>

            {/* Always-visible gradient bottom */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: 'inherit',
              background: 'linear-gradient(to top, rgba(26,18,24,0.85) 0%, rgba(26,18,24,0.2) 45%, transparent 70%)',
            }} />

            {/* Count badge top-right */}
            <div style={{
              position: 'absolute', top: '1rem', right: '1rem', zIndex: 3,
            }}>
              <span className="badge" style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: '0.65rem',
              }}>
                {col.count}
              </span>
            </div>

            {/* Slide-up overlay info */}
            <div className="card-overlay" style={{ borderRadius: 'inherit' }}>
              <div className="card-info">
                <div className="heading-sub" style={{ color: 'rgba(255,255,255,0.55)', marginBottom: '0.5rem', fontSize: '0.65rem' }}>
                  {t.collectionsTag}
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-serif)', fontSize: '1.4rem', fontWeight: 400,
                  color: '#fff', marginBottom: '0.6rem',
                }}>
                  {col.name}
                </h3>
                <p style={{
                  fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)',
                  lineHeight: 1.6, marginBottom: '1.25rem',
                }}>
                  {col.description}
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '0.72rem', fontWeight: 700,
                  color: '#E8B4B8', textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  {t.viewOnFb} <ArrowRight size={13} />
                </div>
              </div>
            </div>

            {/* Bottom always-visible name */}
            <div style={{
              position: 'absolute', bottom: '1.5rem', left: '1.5rem', zIndex: 2,
              transition: 'opacity 0.3s',
            }}>
              <h3 style={{
                fontFamily: 'var(--font-serif)', fontSize: '1.25rem',
                color: '#fff', fontWeight: 400,
              }}>
                {col.name}
              </h3>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
